import { HeartIcon } from '@heroicons/react/24/outline'
import {
  ArrowUturnLeftIcon,
  HeartIcon as HeartIconSolid,
} from '@heroicons/react/24/solid'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import apiService from '../api/apiService'
import { useAuth } from '../contexts/AuthContext'
import { useUndo } from '../contexts/UndoContext'
import MealCalendar from './MealCalendar'
import RecipeForm from './RecipeForm'
import {
  FilterPanel,
  MobileIngredientsPanel,
  RecipeCard,
  RecipeCardSkeleton,
} from './RecipeList/index'
import SortDropdown from './RecipeList/SortDropdown'
import { Badge, Button, Card, Grid } from './ui'

const RecipeList = forwardRef(
  (
    {
      showOnlyFavorites: initialShowFavorites = false,
      initialShowCalendar = false,
      showRecipeForm: externalShowRecipeForm,
      setShowRecipeForm: externalSetShowRecipeForm,
      onAddToIngredients,
      sharedRecipeId,
    },
    ref
  ) => {
    const [recipes, setRecipes] = useState([])
    const [favorites, setFavorites] = useState(new Set())
    const [showOnlyFavorites, setShowOnlyFavorites] =
      useState(initialShowFavorites)
    const [editingRecipe, setEditingRecipe] = useState(null)
    const [copyingRecipe, setCopyingRecipe] = useState(null)
    const [expandedRecipeId, setExpandedRecipeId] = useState(null)
    const [selectedRecipes, setSelectedRecipes] = useState([])
    const [ingredientList, setIngredientList] = useState([])
    const [realIngredientCount, setRealIngredientCount] = useState(0)
    const [copyMessage, setCopyMessage] = useState('')
    const [loading, setLoading] = useState(true)
    const [loadingIngredients, setLoadingIngredients] = useState(false)
    const [deletingRecipeId, setDeletingRecipeId] = useState(null)
    const [showAuthPopup, setShowAuthPopup] = useState(false)
    const [dbUser, setDbUser] = useState(null)
    const [ratings, setRatings] = useState({})
    const [userRatings, setUserRatings] = useState({})
    const [ratingCounts, setRatingCounts] = useState({})
    const [showCalendar, setShowCalendar] = useState(initialShowCalendar)
    const [windowWidth, setWindowWidth] = useState(window.innerWidth)

    const [showFilters, setShowFilters] = useState(false)
    const [filterByCreator, setFilterByCreator] = useState('')
    const [filterByRating, setFilterByRating] = useState(0)
    const [filterByCookingTime, setFilterByCookingTime] = useState('')
    const [filterByIngredient, setFilterByIngredient] = useState('')
    const [filterByIngredients, setFilterByIngredients] = useState([])
    const [ingredientInput, setIngredientInput] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [uniqueCreators, setUniqueCreators] = useState([])
    const [uniqueIngredients, setUniqueIngredients] = useState([])

    const [sortBy, setSortBy] = useState('created_at')
    const [sortDirection, setSortDirection] = useState('desc')
    const [showSortDropdown, setShowSortDropdown] = useState(false)

    const [showCreatorDropdown, setShowCreatorDropdown] = useState(false)
    const [showRatingDropdown, setShowRatingDropdown] = useState(false)
    const [showCookingTimeDropdown, setShowCookingTimeDropdown] =
      useState(false)
    const [showSearchInput, setShowSearchInput] = useState(false)

    const [selectedMealInfo, setSelectedMealInfo] = useState(null)
    const [isHeaderSticky, setIsHeaderSticky] = useState(false)

    const { user } = useAuth()
    const {
      addToUndoStack,
      undo: originalUndo,
      canUndo,
      setRefresh,
      undoStack,
    } = useUndo()

    const inProgressRef = useRef({
      fetchRecipes: false,
      fetchFavorites: false,
      fetchRatings: false,
    })

    useEffect(() => {
      const handleResize = () => {
        setWindowWidth(window.innerWidth)
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
      let ticking = false

      const handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            const header = document.querySelector('.recipe-header')
            if (header) {
              const headerRect = header.getBoundingClientRect()
              setIsHeaderSticky(headerRect.top <= 16)
            }
            ticking = false
          })

          ticking = true
        }
      }

      window.addEventListener('scroll', handleScroll, { passive: true })
      return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const handleCalendarToggle = () => {
      setShowCalendar((prevState) => !prevState)
    }



    const refreshData = useCallback(async () => {
      if (inProgressRef.current.fetchRecipes) return

      try {
        inProgressRef.current.fetchRecipes = true

        // Start loading state
        setLoading(true)
        // Track when we started loading
        const loadStartTime = Date.now()

        // Fetch core data and user data in parallel first
        const corePromises = [
          apiService.getRecipesFull(),
          apiService.getAllRatings(),
          user?.email ? apiService.getUserByEmail(user.email) : Promise.resolve(null),
        ]

        const [recipesData, ratingsData, userData] = await Promise.all(corePromises)

        // Set dbUser immediately
        if (userData?.id) {
          setDbUser(userData)
        }

        // Fetch favorites separately (this is unavoidable since we need user ID first)
        const favoritesData = userData?.id 
          ? await apiService.getFavorites(userData.id)
          : []

        // Process ratings
        const averages = {}
        const counts = {}
        const userRatingMap = {}

        ratingsData.forEach((rating) => {
          if (userData && rating.user_id === userData.id) {
            userRatingMap[rating.recipe_id] = rating.rating
          }

          if (!averages[rating.recipe_id]) {
            averages[rating.recipe_id] = { sum: 0, count: 0 }
          }
          averages[rating.recipe_id].sum += rating.rating
          averages[rating.recipe_id].count += 1
          counts[rating.recipe_id] = (counts[rating.recipe_id] || 0) + 1
        })

        Object.keys(averages).forEach((recipeId) => {
          averages[recipeId] = averages[recipeId].sum / averages[recipeId].count
        })

        // Only update states if the data has actually changed
        const newRecipes = recipesData || []
        const newFavorites = new Set(
          favoritesData.map((fav) => fav.recipe_id)
        )

        setRecipes(newRecipes)
        setFavorites(newFavorites)

        setRatings((prevRatings) => {
          if (JSON.stringify(prevRatings) === JSON.stringify(averages)) {
            return prevRatings
          }
          return averages
        })

        setRatingCounts((prevCounts) => {
          if (JSON.stringify(prevCounts) === JSON.stringify(counts)) {
            return prevCounts
          }
          return counts
        })

        setUserRatings((prevUserRatings) => {
          if (
            JSON.stringify(prevUserRatings) === JSON.stringify(userRatingMap)
          ) {
            return prevUserRatings
          }
          return userRatingMap
        })

      } catch (error) {
        console.error('Refresh error:', error)
        setRecipes([])
        setFavorites(new Set())
        setRatings({})
        setRatingCounts({})
        setUserRatings({})
      } finally {
        inProgressRef.current.fetchRecipes = false
        setLoading(false)
      }
    }, [user])

    // Effect for setting up refresh callback and initial data load
    useEffect(() => {
      let isMounted = true

      const setup = async () => {
        // Set up the refresh callback once
        setRefresh(() => refreshData)

        // Initial data load
        if (isMounted) {
          await refreshData()
        }
      }

      setup()

      return () => {
        isMounted = false
        setRefresh(() => Promise.resolve())
      }
    }, [refreshData, setRefresh])

    useEffect(() => {
      if (recipes.length > 0) {
        const creators = recipes
          .filter((recipe) => recipe.users)
          .map((recipe) => ({
            id: recipe.users.id,
            name: `${recipe.users.first_name || ''} ${recipe.users.last_name || ''}`.trim(),
            image_url: recipe.users.image_url,
          }))
          .reduce((unique, creator) => {
            if (!unique.some((u) => u.id === creator.id)) {
              unique.push(creator)
            }
            return unique
          }, [])

        setUniqueCreators(creators)

        const allIngredients = new Set()
        recipes.forEach((recipe) => {
          try {
            if (recipe.ingredients) {
              const parsedIngredients = JSON.parse(recipe.ingredients)
              parsedIngredients.forEach((ing) => {
                if (ing.item && ing.item.trim()) {
                  allIngredients.add(ing.item.trim().toLowerCase())
                }
              })
            }
          } catch (error) {
            // Skip if can't parse ingredients
          }
        })

        setUniqueIngredients(Array.from(allIngredients).sort())
      }
    }, [recipes])

    useEffect(() => {
      if (externalShowRecipeForm !== undefined) {
        if (externalShowRecipeForm === false) {
          setEditingRecipe(null)
          setCopyingRecipe(null)
        }
      }
    }, [externalShowRecipeForm])

    const handleRecipeSaved = () => {
      setEditingRecipe(null)
      setCopyingRecipe(null)
      if (externalSetShowRecipeForm) {
        externalSetShowRecipeForm(false)
      }

      // Refresh data
      refreshData()
    }

    const handleAuthAction = (action) => {
      if (!user) {
        setShowAuthPopup(true)
        return false
      }
      return true
    }

    const handleEdit = async (recipe) => {
      if (!handleAuthAction()) return
      if (!dbUser?.id) return

      if (recipe.users?.id !== dbUser.id) {
        throw new Error('You do not have permission to edit this recipe.')
      }

      setEditingRecipe(recipe)
      setCopyingRecipe(null)
      if (externalSetShowRecipeForm) {
        externalSetShowRecipeForm(true)
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 100)
      }
    }

    const handleCopyRecipe = (recipe) => {
      if (!handleAuthAction()) return
      if (!dbUser?.id) return

      setCopyingRecipe({
        ...recipe,
        name: `${recipe.name}`,
        user_id: dbUser.id,
        users: {
          id: dbUser.id,
          first_name: user.given_name,
          last_name: user.family_name,
          image_url: user.picture,
        },
      })
      setEditingRecipe(null)
      if (externalSetShowRecipeForm) {
        externalSetShowRecipeForm(true)
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 100)
      }
    }

    const handleDelete = async (id) => {
      if (!handleAuthAction()) return
      if (!dbUser?.id) return

      if (
        !window.confirm(
          'Are you sure you want to delete this recipe?\nIt will permanently lose its ratings, which cannot be undone.'
        )
      ) {
        return
      }

      try {
        setDeletingRecipeId(id)

        // Fetch the recipe to be deleted for the undo stack
        const recipeToDelete = await apiService.getRecipesFull().then((data) =>
          data.find((recipe) => recipe.id === id && recipe.user_id === dbUser.id)
        )

        if (!recipeToDelete) {
          throw new Error('You do not have permission to delete this recipe.')
        }

        // Delete the recipe using API service
        await apiService.deleteRecipeWithUserId(id, dbUser.id)

        addToUndoStack({
          type: 'delete',
          data: recipeToDelete,
        })

        setSelectedRecipes((prevSelected) =>
          prevSelected.filter((recipeId) => recipeId !== id)
        )
        refreshData()
      } catch (error) {
        console.error('Error deleting recipe:', error)
      } finally {
        setDeletingRecipeId(null)
      }
    }

    const handleToggleExpand = (id) => {
      setExpandedRecipeId((prevId) => {
        // If we're expanding the recipe (it wasn't expanded before)
        if (prevId !== id) {
          // Use requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            const card = document.getElementById(`recipe-${id}`)
            if (card) {
              const cardRect = card.getBoundingClientRect()
              const scrollTo = window.pageYOffset + cardRect.top - 80 // 80px padding from top

              window.scrollTo({
                top: scrollTo,
                behavior: 'smooth',
              })

              // Add a subtle highlight effect
              card.classList.add('animate-pulse-border')
              setTimeout(() => {
                card.classList.remove('animate-pulse-border')
              }, 800)
            }
          })

          return id
        } else {
          // When collapsing, scroll back to the recipe card
          requestAnimationFrame(() => {
            const card = document.getElementById(`recipe-${prevId}`)
            if (card) {
              const cardRect = card.getBoundingClientRect()
              const scrollTo = window.pageYOffset + cardRect.top - 80 // 80px padding from top

              window.scrollTo({
                top: scrollTo,
                behavior: 'smooth',
              })
            }
          })

          return null
        }
      })
    }

    const handleAddRecipeClick = () => {
      if (!handleAuthAction()) return
      setEditingRecipe(null)
      setCopyingRecipe(null)
      if (externalSetShowRecipeForm) {
        externalSetShowRecipeForm(true)
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 100)
      }
    }

    const handleRecipeClick = (recipeId) => {
      const isAlreadySelected = selectedRecipes.includes(recipeId)

      setSelectedRecipes((prevSelected) => {
        if (isAlreadySelected) {
          return prevSelected.filter((id) => id !== recipeId)
        } else {
          const recipeCard = document.getElementById(`recipe-${recipeId}`)
          if (recipeCard) {
            recipeCard.classList.add('animate-pulse-border')
            setTimeout(() => {
              recipeCard.classList.remove('animate-pulse-border')
            }, 400)
          }

          return [...prevSelected, recipeId]
        }
      })
    }

    const handleCopyIngredients = async () => {
      if (ingredientList.length === 0) return

      setCopyMessage('Sorting...')
      setLoadingIngredients(true)

      try {
        let ingredientsToCopy = ingredientList

        try {
          // Try to sort ingredients with the API
          setCopyMessage('Sorting...')

          // Call the backend API to sort ingredients using OpenRouter
          const response = await fetch('/api/sort-ingredients', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ ingredients: ingredientsToCopy }),
          })

          if (response.ok) {
            const { sortedIngredients: sorted } = await response.json()
            ingredientsToCopy = sorted
            setIngredientList(ingredientsToCopy.trim().split('\n'))
          } else {
            // Log failure but continue with unsorted ingredients
            console.error(
              'Ingredient sorting failed, using unsorted ingredients'
            )
          }
        } catch (sortError) {
          // Log sorting error but continue with unsorted ingredients
          console.error('Sorting error:', sortError)
        }

        // Copy to clipboard
        await navigator.clipboard.writeText(ingredientsToCopy)

        setCopyMessage('Copied!')

        // Clear message after 3 seconds
        setTimeout(() => {
          setCopyMessage('')
        }, 3000)
      } catch (error) {
        console.error('Copy failed:', error)
        setCopyMessage('Copy failed')

        // Clear error message after 3 seconds
        setTimeout(() => {
          setCopyMessage('')
        }, 3000)
      } finally {
        setLoadingIngredients(false)
      }
    }

    const handleToggleFavorite = async (recipeId) => {
      if (!handleAuthAction()) return
      if (!dbUser?.id) return

      const isFavorited = favorites.has(recipeId)

      try {
        // Optimistically update the UI immediately
        setFavorites((prev) => {
          const next = new Set(prev)
          if (isFavorited) {
            next.delete(recipeId)
          } else {
            next.add(recipeId)
          }
          return next
        })

        // Make the actual API call, but don't wait for it to update UI
        if (isFavorited) {
          await apiService.toggleFavorite(dbUser.id, recipeId, 'remove')

          addToUndoStack({
            type: 'favorite',
            data: {
              userId: dbUser.id,
              recipeId,
              isFavorited: true,
            },
          })
        } else {
          await apiService.toggleFavorite(dbUser.id, recipeId, 'add')

          addToUndoStack({
            type: 'favorite',
            data: {
              userId: dbUser.id,
              recipeId,
              isFavorited: false,
            },
          })
        }

        // We removed the refreshData call here since we're already updating the UI
      } catch (error) {
        console.error('Error toggling favorite:', error)
        // Revert optimistic update on error
        setFavorites((prev) => {
          const next = new Set(prev)
          if (isFavorited) {
            next.add(recipeId) // Restore the favorite
          } else {
            next.delete(recipeId) // Remove the favorite
          }
          return next
        })
      }
    }

    const handleRating = async (recipeId, rating) => {
      if (!handleAuthAction()) return
      if (!dbUser?.id) return

      // Store the old rating for potential rollback
      const oldRating = userRatings[recipeId] || 0

      // Calculate new average rating optimistically
      const optimisticRatings = { ...ratings }
      const optimisticRatingCounts = { ...ratingCounts }

      // Get current average and count for this recipe
      const currentAvg = optimisticRatings[recipeId] || 0
      const currentCount = optimisticRatingCounts[recipeId] || 0

      try {
        // Calculate the new average rating
        let newAvg, newCount

        if (oldRating > 0) {
          // If user is updating an existing rating
          // Remove old rating from average and add new one
          const totalValue = currentAvg * currentCount
          const newTotal = totalValue - oldRating + rating
          newAvg = newTotal / currentCount
          newCount = currentCount
        } else {
          // If user is adding a new rating
          const totalValue = currentAvg * currentCount
          const newTotal = totalValue + rating
          newCount = currentCount + 1
          newAvg = newTotal / newCount
        }

        // Update UI state optimistically
        setUserRatings((prev) => ({
          ...prev,
          [recipeId]: rating,
        }))

        setRatings((prev) => ({
          ...prev,
          [recipeId]: newAvg,
        }))

        setRatingCounts((prev) => ({
          ...prev,
          [recipeId]: newCount,
        }))

        // Make the actual API call
        if (oldRating > 0) {
          await apiService.addOrUpdateRating(dbUser.id, recipeId, rating)
        } else {
          await apiService.addOrUpdateRating(dbUser.id, recipeId, rating)
        }

        // We no longer need to refresh all data
      } catch (error) {
        console.error('Rating error:', error)
        // Revert optimistic updates if error occurs
        setUserRatings((prev) => ({
          ...prev,
          [recipeId]: oldRating,
        }))

        setRatings((prev) => ({
          ...prev,
          [recipeId]: currentAvg,
        }))

        setRatingCounts((prev) => ({
          ...prev,
          [recipeId]: currentCount,
        }))
      }
    }

    const convertToBaseUnit = (amount, unit) => {
      if (!amount) return { amount, unit }

      if (unit === 'kg') return { amount: amount * 1000, unit: 'g' }
      if (unit === 'g' && amount >= 1000)
        return { amount: amount / 1000, unit: 'kg' }

      if (unit === 'L') return { amount: amount * 1000, unit: 'mL' }
      if (unit === 'mL' && amount >= 1000)
        return { amount: amount / 1000, unit: 'L' }

      return { amount, unit }
    }

    const pluralizeUnit = (amount, unit) => {
      if (amount <= 1) return unit

      const pluralUnits = {
        cup: 'cups',
        pinch: 'pinches',
        piece: 'pieces',
        slice: 'slices',
        tablespoon: 'tablespoons',
        teaspoon: 'teaspoons',
        tbsp: 'tbsp',
        tsp: 'tsp',
        clove: 'cloves',
        leaf: 'leaves',
        sprig: 'sprigs',
        bunch: 'bunches',
        stalk: 'stalks',
        stick: 'sticks',
        can: 'cans',
        package: 'packages',
      }

      return pluralUnits[unit] || unit
    }

    useEffect(() => {
      // Early return if no recipes selected - avoid unnecessary async work
      if (selectedRecipes.length === 0) {
        setIngredientList([])
        setRealIngredientCount(0)
        setLoadingIngredients(false)
        return
      }

      const fetchIngredients = async () => {
        try {
          setLoadingIngredients(true)

          // Use the dedicated ingredients endpoint instead of fetching all recipes
          const ingredientsData = await apiService.getRecipeIngredients(selectedRecipes);

          if (ingredientsData.length === 0) {
            console.error('No recipes found for selected IDs:', selectedRecipes);
            throw new Error('No recipes found')
          }

          const ingredientsByName = {}

          ingredientsData.forEach((recipe) => {
            try {
              if (recipe.ingredients === null || recipe.ingredients === undefined) {
                console.warn(`Recipe ${recipe.id} has null/undefined ingredients:`, recipe.ingredients);
                return;
              }

              const parsedIngredients = JSON.parse(recipe.ingredients || '[]')
              parsedIngredients.forEach(({ item, amount, unit }) => {
                if (!item) return

                const normalizedItem = item.toLowerCase().trim()

                if (!ingredientsByName[normalizedItem]) {
                  ingredientsByName[normalizedItem] = {
                    displayName: item,
                    measurements: {},
                  }
                }

                if (!amount) {
                  ingredientsByName[normalizedItem].measurements[''] =
                    (ingredientsByName[normalizedItem].measurements[''] ||
                      0) + 1
                  return
                }

                const unitKey = unit || ''

                ingredientsByName[normalizedItem].measurements[unitKey] =
                  (ingredientsByName[normalizedItem].measurements[unitKey] ||
                    0) + parseFloat(amount)
              })
            } catch (error) {
              console.warn(`Failed to parse ingredients as JSON for recipe ${recipe.id}, trying alternative parsing:`, error);
              try {
                const ingredientString = recipe.ingredients || ''
                const cleanString = ingredientString.replace(/^\[|\]$/g, '')
                const ingredients = cleanString.split('},{').map((item) => {
                  const cleanItem = item.replace(/^{/, '{').replace(/}$/, '}')
                  return JSON.parse(cleanItem)
                })

                ingredients.forEach(({ item, amount, unit }) => {
                  if (!item) return

                  const normalizedItem = item.toLowerCase().trim()

                  if (!ingredientsByName[normalizedItem]) {
                    ingredientsByName[normalizedItem] = {
                      displayName: item,
                      measurements: {},
                    }
                  }

                  if (!amount) {
                    ingredientsByName[normalizedItem].measurements[''] =
                      (ingredientsByName[normalizedItem].measurements[''] ||
                        0) + 1
                    return
                  }

                  const unitKey = unit || ''

                  ingredientsByName[normalizedItem].measurements[unitKey] =
                    (ingredientsByName[normalizedItem].measurements[
                      unitKey
                    ] || 0) + parseFloat(amount)
                })
              } catch (e) {
                console.warn(`Failed to parse ingredients as malformed JSON for recipe ${recipe.id}, trying comma-separated parsing:`, e);
                const ingredients = (recipe.ingredients || '')
                  .split(',')
                  .map((ing) => ing.trim())
                ingredients.forEach((ingredient) => {
                  if (!ingredient) return

                  const normalizedItem = ingredient.toLowerCase().trim()

                  if (!ingredientsByName[normalizedItem]) {
                    ingredientsByName[normalizedItem] = {
                      displayName: ingredient,
                      measurements: { '': 1 },
                    }
                  } else {
                    ingredientsByName[normalizedItem].measurements[''] =
                      (ingredientsByName[normalizedItem].measurements[''] ||
                        0) + 1
                  }
                })
              }
            }
          })

          // Count unique ingredient names
          const uniqueIngredientCount = Object.keys(ingredientsByName).length
          setRealIngredientCount(uniqueIngredientCount)

          const formattedIngredients = []

          Object.values(ingredientsByName).forEach(
            ({ displayName, measurements }) => {
              if (
                Object.keys(measurements).length === 1 &&
                measurements[''] !== undefined
              ) {
                if (measurements[''] === 1) {
                  formattedIngredients.push(displayName)
                } else {
                  formattedIngredients.push(
                    `${measurements['']}x ${displayName}`
                  )
                }
                return
              }

              if (measurements['g'] && measurements['kg']) {
                const totalGrams =
                  measurements['g'] + measurements['kg'] * 1000
                const converted = convertToBaseUnit(totalGrams, 'g')
                delete measurements['g']
                delete measurements['kg']
                measurements[converted.unit] = converted.amount
              }

              if (measurements['mL'] && measurements['L']) {
                const totalML = measurements['mL'] + measurements['L'] * 1000
                const converted = convertToBaseUnit(totalML, 'mL')
                delete measurements['mL']
                delete measurements['L']
                measurements[converted.unit] = converted.amount
              }

              const measurementTexts = []

              Object.entries(measurements).forEach(([unit, amount]) => {
                if (unit === '') {
                  if (amount > 1) {
                    measurementTexts.push(`${amount}x`)
                  }
                  return
                }

                const { amount: convertedAmount, unit: convertedUnit } =
                  convertToBaseUnit(amount, unit)

                let formattedAmount
                if (Number.isInteger(convertedAmount)) {
                  formattedAmount = convertedAmount
                } else {
                  formattedAmount = Math.round(convertedAmount * 100) / 100
                }

                const displayUnit = pluralizeUnit(
                  formattedAmount,
                  convertedUnit
                )

                const noSpaceUnits = [
                  'g',
                  'kg',
                  'mL',
                  'L',
                  'tsp',
                  'tbsp',
                  'oz',
                  'lb',
                ]
                const hasNoSpace = noSpaceUnits.includes(displayUnit)

                measurementTexts.push(
                  `${formattedAmount}${hasNoSpace ? displayUnit : ` ${displayUnit}`}`
                )
              })

              if (measurementTexts.length > 0) {
                formattedIngredients.push(
                  `${displayName} (${measurementTexts.join(', ')})`
                )
              } else {
                formattedIngredients.push(displayName)
              }
            }
          )

          if (formattedIngredients.length === 0) {
            console.warn('No formatted ingredients found after processing, clearing ingredient list');
            setIngredientList([])
            setLoadingIngredients(false)
            setRealIngredientCount(0)
            return
          }

          // Set ingredient list directly without animation delay
          setIngredientList(formattedIngredients)
          setLoadingIngredients(false)
        } catch (error) {
          console.error('Error fetching ingredients:', error);
          setLoadingIngredients(false)
          setIngredientList([])
          setRealIngredientCount(0)
        }
      }

      fetchIngredients()
    }, [selectedRecipes])

    const applyFilters = (recipeList) => {
      let filteredRecipes = [...recipeList]

      if (showOnlyFavorites) {
        filteredRecipes = filteredRecipes.filter((recipe) =>
          favorites.has(recipe.id)
        )
      }

      if (searchTerm.trim()) {
        filteredRecipes = filteredRecipes.filter(
          (recipe) =>
            (recipe.name &&
              recipe.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (recipe.description &&
              recipe.description
                .toLowerCase()
                .includes(searchTerm.toLowerCase()))
        )
      }

      if (filterByCreator) {
        filteredRecipes = filteredRecipes.filter(
          (recipe) => recipe.users && recipe.users.id === filterByCreator
        )
      }

      if (filterByRating > 0) {
        filteredRecipes = filteredRecipes.filter((recipe) => {
          const avgRating = ratings[recipe.id] || 0
          if (avgRating === 0 || avgRating < filterByRating) {
            return false
          }
          return true
        })
      }

      if (filterByCookingTime) {
        filteredRecipes = filteredRecipes.filter((recipe) => {
          if (!recipe.cooking_time_minutes) {
            return false
          }

          const maxTime = parseInt(filterByCookingTime)
          if (parseInt(recipe.cooking_time_minutes) > maxTime) {
            return false
          }
          return true
        })
      }

      if (filterByIngredients.length > 0) {
        filteredRecipes = filteredRecipes.filter((recipe) => {
          if (!recipe.ingredients) {
            return false
          }

          try {
            const parsedIngredients = JSON.parse(recipe.ingredients)
            const hasAllIngredients = filterByIngredients.every(
              (filterIngredient) =>
                parsedIngredients.some(
                  (ing) =>
                    ing.item &&
                    ing.item
                      .toLowerCase()
                      .includes(filterIngredient.toLowerCase())
                )
            )

            return hasAllIngredients
          } catch (error) {
            return false
          }
        })
      }

      return filteredRecipes
    }

    const filteredRecipes = applyFilters(recipes)

    const sortedRecipes = useMemo(() => {
      return [...filteredRecipes].sort((a, b) => {
        switch (sortBy) {
          case 'avg_rating':
            const ratingA = ratings[a.id] || 0
            const ratingB = ratings[b.id] || 0
            return sortDirection === 'asc'
              ? ratingA - ratingB
              : ratingB - ratingA

          case 'personal_rating':
            const userRatingA = userRatings[a.id] || 0
            const userRatingB = userRatings[b.id] || 0
            return sortDirection === 'asc'
              ? userRatingA - userRatingB
              : userRatingB - userRatingA

          case 'cooking_time':
            const timeA = parseInt(a.cooking_time_minutes) || 0
            const timeB = parseInt(b.cooking_time_minutes) || 0
            return sortDirection === 'asc' ? timeA - timeB : timeB - timeA

          case 'created_at':
          default:
            const dateA = new Date(a.created_at || 0)
            const dateB = new Date(b.created_at || 0)
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA
        }
      })
    }, [filteredRecipes, sortBy, sortDirection, ratings, userRatings])

    const handleResetFilters = () => {
      setFilterByCreator('')
      setFilterByRating(0)
      setFilterByCookingTime('')
      setFilterByIngredient('')
      setFilterByIngredients([])
      setIngredientInput('')
      setSearchTerm('')
      setShowOnlyFavorites(false)
      setShowSearchInput(false)
    }

    const handleToggleSortDirection = () => {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    }

    useEffect(() => {
      const handleClickOutside = (event) => {
        const sortDropdownElement = document.getElementById(
          'sort-dropdown-container'
        )
        if (
          sortDropdownElement &&
          !sortDropdownElement.contains(event.target)
        ) {
          setShowSortDropdown(false)
        }

        // Close mobile search if clicking outside
        if (showSearchInput) {
          const searchContainer = event.target.closest('.recipe-header')
          if (!searchContainer) {
            setShowSearchInput(false)
          }
        }
      }

      if (showSortDropdown || showSearchInput) {
        document.addEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [showSortDropdown, showSearchInput])

    const handleAddIngredientTag = () => {
      if (
        ingredientInput.trim() !== '' &&
        !filterByIngredients.includes(ingredientInput.trim())
      ) {
        setFilterByIngredients([...filterByIngredients, ingredientInput.trim()])
        setIngredientInput('')
      }
    }

    const handleRemoveIngredientTag = (ingredient) => {
      setFilterByIngredients(
        filterByIngredients.filter((ing) => ing !== ingredient)
      )
    }

    const handleIngredientKeyDown = (e) => {
      if (e.key === 'Tab' && ingredientInput.trim() !== '') {
        e.preventDefault()
        handleAddIngredientTag()
      } else if (
        e.key === 'Backspace' &&
        ingredientInput === '' &&
        filterByIngredients.length > 0
      ) {
        const newTags = [...filterByIngredients]
        newTags.pop()
        setFilterByIngredients(newTags)
      }
    }

    const handleClearIngredients = () => {
      setIngredientList([])
      setRealIngredientCount(0)
      setSelectedRecipes([])
    }

    const handleToggleFavorites = () => {
      const newValue = !showOnlyFavorites
      setShowOnlyFavorites(newValue)
    }

    useEffect(() => {
      setShowOnlyFavorites(initialShowFavorites)
    }, [initialShowFavorites])

    const handleAddToCalendar = (recipe, mealNumber) => {
      const today = new Date()
      const sunday = new Date(today)
      sunday.setDate(today.getDate() - today.getDay())

      if (!showCalendar) {
        setShowCalendar(true)
      }

      const dropEvent = {
        dataTransfer: {
          getData: () =>
            JSON.stringify({
              id: recipe.id,
              name: recipe.name,
              image_url: recipe.image_url,
            }),
        },
        preventDefault: () => {},
      }

      setSelectedMealInfo({
        recipe,
        mealNumber,
        dropEvent,
      })


    }

    useEffect(() => {
      if (selectedMealInfo && showCalendar) {
        const { recipe, mealNumber } = selectedMealInfo

        const calendar = document.querySelector('.meal-calendar')
        if (calendar) {
          const mealSlot = calendar.querySelector(
            `[data-meal-number="${mealNumber}"]`
          )
          if (mealSlot) {
            const dropEvt = new Event('drop', { bubbles: true })
            Object.defineProperty(dropEvt, 'dataTransfer', {
              value: {
                getData: () =>
                  JSON.stringify({
                    id: recipe.id,
                    name: recipe.name,
                    image_url: recipe.image_url,
                  }),
              },
            })
            mealSlot.dispatchEvent(dropEvt)
          }
        }

        setSelectedMealInfo(null)
      }
    }, [selectedMealInfo, showCalendar])

    // Function to handle adding ingredients from calendar
    const handleAddIngredientsFromCalendar = (recipeIds) => {
      if (!recipeIds || recipeIds.length === 0) return

      // Update our local state directly with the recipe IDs
      setSelectedRecipes(recipeIds)

      // Call the parent's callback if it exists - this is now optional
      // since we're handling the state update directly
      if (onAddToIngredients) {
        onAddToIngredients(recipeIds)
      }

      // Scroll to ingredients panel on desktop
      if (window.innerWidth >= 1024) {
        const ingredientsPanel = document.querySelector('.meal-calendar')
        if (ingredientsPanel) {
          ingredientsPanel.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      }
    }



    // Wrap the original undo function to ensure data refresh
    const handleUndo = async () => {
      try {
        await originalUndo()
        // Force refresh data after undo completes
        await refreshData()
      } catch (error) {
        console.error('Error during undo operation:', error)
      }
    }

    // Expose handleAddIngredientsFromCalendar to parent components via ref
    useImperativeHandle(ref, () => ({
      handleAddIngredientsDirectly: (recipeIds) => {
        if (!recipeIds || recipeIds.length === 0) return

        // Update directly without calling parent's onAddToIngredients
        setSelectedRecipes(recipeIds)
        
        // Ensure ingredients are loaded immediately
        setLoadingIngredients(true)

        // Get recipes data for these IDs
        const loadIngredientsForRecipes = async () => {
          try {
            // Use the dedicated ingredients endpoint
            const ingredientsData = await apiService.getRecipeIngredients(recipeIds);

            if (ingredientsData.length === 0) {
              throw new Error('No recipes found')
            }

            // Process ingredients
            const ingredientsByName = {}

            ingredientsData.forEach((recipe) => {
              try {
                if (recipe.ingredients === null) return

                const parsedIngredients = JSON.parse(recipe.ingredients || '[]')
                parsedIngredients.forEach(({ item, amount, unit }) => {
                  if (!item) return

                  const normalizedItem = item.toLowerCase().trim()

                  if (!ingredientsByName[normalizedItem]) {
                    ingredientsByName[normalizedItem] = {
                      displayName: item,
                      measurements: {},
                    }
                  }

                  if (!amount) {
                    ingredientsByName[normalizedItem].measurements[''] =
                      (ingredientsByName[normalizedItem].measurements[''] ||
                        0) + 1
                    return
                  }

                  const unitKey = unit || ''

                  ingredientsByName[normalizedItem].measurements[unitKey] =
                    (ingredientsByName[normalizedItem].measurements[unitKey] ||
                      0) + parseFloat(amount)
                })
              } catch (error) {
                console.error('Error processing ingredients:', error)
              }
            })

            // Count unique ingredient names
            const uniqueIngredientCount = Object.keys(ingredientsByName).length
            setRealIngredientCount(uniqueIngredientCount)

            // Format ingredients list
            const formattedIngredients = []

            Object.values(ingredientsByName).forEach(
              ({ displayName, measurements }) => {
                if (
                  Object.keys(measurements).length === 1 &&
                  measurements[''] !== undefined
                ) {
                  if (measurements[''] === 1) {
                    formattedIngredients.push(displayName)
                  } else {
                    formattedIngredients.push(
                      `${measurements['']}x ${displayName}`
                    )
                  }
                  return
                }

                const measurementTexts = []

                Object.entries(measurements).forEach(([unit, amount]) => {
                  if (unit === '') {
                    if (amount > 1) {
                      measurementTexts.push(`${amount}x`)
                    }
                    return
                  }

                  const displayUnit = pluralizeUnit(amount, unit)
                  measurementTexts.push(`${amount} ${displayUnit}`)
                })

                if (measurementTexts.length > 0) {
                  formattedIngredients.push(
                    `${displayName} (${measurementTexts.join(', ')})`
                  )
                } else {
                  formattedIngredients.push(displayName)
                }
              }
            )

            // Set the ingredient list
            setIngredientList(formattedIngredients)
          } catch (error) {
            console.error('Error loading ingredients:', error)
          } finally {
            setLoadingIngredients(false)
          }
        }

        loadIngredientsForRecipes()
      }
    }))

    // Handle shared recipe from URL
    useEffect(() => {
      if (sharedRecipeId && recipes.length > 0) {
        // Convert the sharedRecipeId to the same type as recipe IDs in the data
        // They could be strings in the URL but numbers in the data or vice versa
        const matchingRecipe = recipes.find(
          (recipe) => String(recipe.id) === String(sharedRecipeId)
        )

        if (matchingRecipe) {
          // First set the expandedRecipeId
          setExpandedRecipeId(matchingRecipe.id)

          // Wait a bit longer to ensure the DOM has fully updated and rendered the expanded recipe
          setTimeout(() => {
            const card = document.getElementById(`recipe-${matchingRecipe.id}`)
            if (card) {
              // Get card position and scroll to position it at the top of the viewport with some padding
              const cardRect = card.getBoundingClientRect()
              const scrollTo = window.pageYOffset + cardRect.top - 80 // 80px padding from top

              window.scrollTo({
                top: scrollTo,
                behavior: 'smooth',
              })

              // Add a highlight effect to draw attention
              card.classList.add('animate-pulse-border')
              setTimeout(() => {
                card.classList.remove('animate-pulse-border')
              }, 1500)
            }
          }, 800)
        }
      }
    }, [sharedRecipeId, recipes])

    return (
      <div className="container mx-auto max-w-full px-1 sm:px-1.5 lg:px-2">
        <div className={`space-y-4`}>
          <div
            className={`recipe-header sticky z-50 bg-white rounded-lg p-3 transition-all duration-300 ease-in-out mt-4 ${
              isHeaderSticky
                ? 'shadow-2xl top-4 bg-opacity-95 max-w-[95%] mx-auto'
                : 'shadow-sm mb-4'
            } border-gray-200 border`}
            style={{
              pointerEvents: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div className="flex justify-between items-center w-full">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
                {showOnlyFavorites ? 'Favorites' : 'Recipes'}
              </h1>
              <div className="flex flex-wrap items-center gap-2 justify-end flex-shrink-0">
                {/* Desktop search input */}
                <div className="hidden sm:flex relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-32 pl-8 pr-6 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 focus:w-48"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Mobile search button */}
                <button
                  onClick={() => setShowSearchInput(!showSearchInput)}
                  className={`sm:hidden flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 ${
                    searchTerm.trim() || showSearchInput
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </button>

                <div className="relative" id="sort-dropdown-container">
                  <button
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 ${
                      sortBy !== 'created_at' || sortDirection !== 'desc'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M3 3a1 1 0 000 2h11a1 1 0 100-2H3zM3 7a1 1 0 000 2h7a1 1 0 100-2H3zM3 11a1 1 0 100 2h4a1 1 0 100-2H3zM15 8a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L15 13.586V8z" />
                    </svg>
                    <span className="hidden sm:inline">Sort</span>
                  </button>
                  {showSortDropdown && (
                    <SortDropdown
                      sortBy={sortBy}
                      setSortBy={setSortBy}
                      sortDirection={sortDirection}
                      handleToggleSortDirection={handleToggleSortDirection}
                      showSortDropdown={showSortDropdown}
                      setShowSortDropdown={setShowSortDropdown}
                      user={user}
                    />
                  )}
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 ${
                    filterByCreator ||
                    filterByRating > 0 ||
                    filterByCookingTime ||
                    filterByIngredient ||
                    searchTerm.trim()
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="hidden sm:inline">Filter</span>
                </button>

                {user && (
                  <button
                    onClick={handleCalendarToggle}
                    className={`hidden lg:flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 ${
                      showCalendar
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="hidden sm:inline">Calendar</span>
                  </button>
                )}

                {user && (
                  <button
                    onClick={handleToggleFavorites}
                    className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 ${
                      showOnlyFavorites
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {showOnlyFavorites ? (
                      <HeartIconSolid className="h-4 w-4 text-red-500" />
                    ) : (
                      <HeartIcon className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">Favorites</span>
                  </button>
                )}

                <button
                  onClick={handleUndo}
                  disabled={!canUndo}
                  className={`bg-gray-100 flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 text-gray-700 ${undoStack.length > 0 ? 'hover:bg-gray-200' : 'hidden'}`}
                >
                  <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Undo</span>
                </button>
              </div>
            </div>

            {showSearchInput && (
              <div className="sm:hidden mt-3">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search recipes by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}

            {showFilters && (
              <FilterPanel
                showFilters={showFilters}
                filterByCreator={filterByCreator}
                setFilterByCreator={setFilterByCreator}
                filterByRating={filterByRating}
                setFilterByRating={setFilterByRating}
                filterByCookingTime={filterByCookingTime}
                setFilterByCookingTime={setFilterByCookingTime}
                filterByIngredients={filterByIngredients}
                setFilterByIngredients={setFilterByIngredients}
                ingredientInput={ingredientInput}
                setIngredientInput={setIngredientInput}
                handleAddIngredientTag={handleAddIngredientTag}
                handleRemoveIngredientTag={handleRemoveIngredientTag}
                handleIngredientKeyDown={handleIngredientKeyDown}
                uniqueCreators={uniqueCreators}
                uniqueIngredients={uniqueIngredients}
                showCreatorDropdown={showCreatorDropdown}
                setShowCreatorDropdown={setShowCreatorDropdown}
                showRatingDropdown={showRatingDropdown}
                setShowRatingDropdown={setShowRatingDropdown}
                showCookingTimeDropdown={showCookingTimeDropdown}
                setShowCookingTimeDropdown={setShowCookingTimeDropdown}
                handleResetFilters={handleResetFilters}
                showOnlyFavorites={showOnlyFavorites}
              />
            )}
          </div>

          {user && externalShowRecipeForm && (
            <div
              className={`mb-4 ${showCalendar && user ? '' : ingredientList.length > 0 || loadingIngredients ? '' : 'lg:max-w-7xl lg:mx-auto'}`}
            >
              <RecipeForm
                recipe={editingRecipe}
                copyingRecipe={copyingRecipe}
                onRecipeSaved={handleRecipeSaved}
                onCancel={() => {
                  setEditingRecipe(null)
                  setCopyingRecipe(null)
                  if (externalSetShowRecipeForm) {
                    externalSetShowRecipeForm(false)
                  }
                }}
              />
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-4">
            <div
              className={`w-full ${showCalendar && user ? 'lg:w-7/8' : ingredientList.length > 0 || loadingIngredients ? 'lg:w-2/3' : ''}`}
            >
              {!loading && filteredRecipes.length === 0 && showOnlyFavorites ? (
                <div className="text-center py-8 text-gray-500 bg-white rounded-lg shadow-sm p-4">
                  No favorite recipes yet. Click the heart icon on a recipe to
                  add it to your favorites!
                </div>
              ) : !loading &&
                filteredRecipes.length === 0 &&
                searchTerm.trim() ? (
                <div className="text-center py-8 text-gray-500 bg-white rounded-lg shadow-sm p-4">
                  <div className="flex flex-col items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-12 w-12 text-gray-400 mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-2">
                      No recipes found
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      No recipes match "{searchTerm}". Try a different search
                      term.
                    </p>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-green-600 hover:text-green-700 font-medium text-sm"
                    >
                      Clear search
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={`w-full lg:flex-1 ${showCalendar ? 'calendar-visible' : ''}`}
                >
                  <Grid cols={3} gap="md">
                    {!recipes.length ? (
                      <>
                        {[...Array(6)].map((_, index) => (
                          <RecipeCardSkeleton key={`skeleton-${index}`} />
                        ))}
                      </>
                    ) : (
                      <>
                        {user && !showOnlyFavorites && windowWidth >= 1024 && (
                          <div>
                            <Card
                              onClick={handleAddRecipeClick}
                              className={`add-recipe-card cursor-pointer flex flex-col h-full ${recipes.length === 0 ? 'empty-state' : ''}`}
                            >
                              <div className="flex-grow flex items-center justify-center p-4">
                                <div className="add-recipe-plus text-6xl font-light transition-transform group-hover:scale-110 group-hover:rotate-90 duration-300">
                                  +
                                </div>
                              </div>
                            </Card>
                          </div>
                        )}

                        {sortedRecipes.map((recipe, index) => {
                          const addCardOffset =
                            user && !showOnlyFavorites ? 1 : 0
                          const actualIndex = index + addCardOffset
                          const gridPos = (actualIndex % 3) + 1
                          const rowPos = Math.floor(actualIndex / 3) + 1

                          let gridColumnValue
                          let gridRowValue

                          if (expandedRecipeId === recipe.id) {
                            if (windowWidth < 1024) {
                              gridColumnValue = '1 / -1'
                              gridRowValue = `${rowPos} / span 2`
                            } else {
                              if (gridPos === 3) {
                                gridColumnValue = '2 / 4'
                              } else if (gridPos === 2) {
                                gridColumnValue = '2 / 4'
                              } else {
                                gridColumnValue = '1 / 3'
                              }
                              gridRowValue = `${rowPos} / span 2`
                            }
                          }

                          return (
                            <div
                              key={recipe.id}
                              className="transition-all duration-300"
                              style={{
                                gridColumn: gridColumnValue,
                                gridRow: gridRowValue,
                              }}
                              data-grid-pos={`col:${gridPos}-row:${rowPos}`}
                            >
                              <RecipeCard
                                recipe={recipe}
                                isSelected={selectedRecipes.includes(recipe.id)}
                                isExpanded={expandedRecipeId === recipe.id}
                                expandedRecipeId={expandedRecipeId}
                                favorites={favorites}
                                ratings={ratings}
                                ratingCounts={ratingCounts}
                                userRatings={userRatings}
                                user={user}
                                dbUser={dbUser}
                                handleRecipeClick={handleRecipeClick}
                                handleToggleExpand={handleToggleExpand}
                                handleToggleFavorite={handleToggleFavorite}
                                handleEdit={handleEdit}
                                handleCopyRecipe={handleCopyRecipe}
                                handleDelete={handleDelete}
                                handleRating={handleRating}
                                deletingRecipeId={deletingRecipeId}
                                onAddToCalendar={handleAddToCalendar}
                                onAddToIngredients={
                                  handleAddIngredientsFromCalendar
                                }
                              />
                            </div>
                          )
                        })}
                      </>
                    )}
                  </Grid>
                </div>
              )}
            </div>

            {showCalendar && user && (
              <div
                className={`w-full ${ingredientList.length > 0 || loadingIngredients ? 'lg:w-1/3 hidden lg:block' : 'lg:w-1/3'}`}
              >
                <div className="lg:sticky lg:top-20">
                  <div className="bg-white shadow-sm rounded-lg border-b border-gray-200 overflow-hidden meal-calendar">
                    <MealCalendar
                      isVisible={showCalendar}
                      onAddToIngredients={handleAddIngredientsFromCalendar}
                    />
                  </div>
                </div>
              </div>
            )}

            {(ingredientList.length > 0 || loadingIngredients) && (
              <div
                className={`hidden lg:block w-full ingredients-panel ${showCalendar && user ? 'lg:w-1/6' : 'lg:w-1/3'}`}
              >
                <div className="rounded-lg lg:sticky lg:top-20">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-h-[calc(100vh-6rem)] overflow-y-auto">
                    <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                      <h2 className="text-xl font-semibold text-gray-800">
                        Ingredients
                      </h2>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="text"
                          onClick={handleCopyIngredients}
                          disabled={loadingIngredients}
                          className={`flex-1 text-green-600 font-medium text-xs ${copyMessage ? 'bg-green-50' : 'hover:bg-green-50'} px-3 transition-colors duration-200`}
                          size="sm"
                        >
                          {copyMessage || 'Copy'}
                        </Button>
                        <Button
                          variant="text"
                          onClick={handleClearIngredients}
                          disabled={
                            loadingIngredients || ingredientList.length === 0
                          }
                          className="flex-1 text-red-600 font-medium text-xs hover:bg-red-50 px-3 transition-colors duration-200"
                          size="sm"
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {loadingIngredients
                          ? ingredientList.map((ingredient, index) => (
                              <Badge
                                key={`loading-${index}`}
                                variant="success"
                                className="placeholder text-base px-3 py-1.5 text-center"
                              >
                                {ingredient}
                              </Badge>
                            ))
                          : ingredientList.map((ingredient, index) => (
                              <Badge
                                key={`real-${index}`}
                                variant="default"
                                className="ingredient-item text-base px-3 py-1.5 text-center"
                                style={{ animationDelay: `${index * 50}ms` }}
                              >
                                {ingredient}
                              </Badge>
                            ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {showAuthPopup &&
          ReactDOM.createPortal(
            <div
              className="auth-popup-overlay"
              onClick={() => setShowAuthPopup(false)}
            >
              <style jsx global>{`
                body {
                  overflow: hidden;
                }
                .auth-popup-overlay {
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  width: 100%;
                  height: 100%;
                  background-color: rgba(0, 0, 0, 0.8);
                  z-index: 99999;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
              `}</style>
              <div
                className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Sign In Required
                </h2>
                <p className="text-gray-600 mb-6">
                  You must be signed in to manage a recipe.
                </p>
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    onClick={() => setShowAuthPopup(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>,
            document.body
          )}

        <MobileIngredientsPanel
          ingredientList={ingredientList}
          loadingIngredients={loadingIngredients}
          handleCopyIngredients={handleCopyIngredients}
          handleClearIngredients={handleClearIngredients}
          copyMessage={copyMessage}
          selectedRecipesCount={selectedRecipes.length}
          realIngredientCount={realIngredientCount}
        />
      </div>
    )
  }
)

export default RecipeList
